import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const galleryImages = [
  {
    src: 'https://prcdn.freetls.fastly.net/release_image/162516/4/162516-4-cacd0c55061626febe7676024dd82ba8-1920x1280.png?format=jpeg&auto=webp&fit=bounds&width=1200&height=800',
    alt: 'Echo installation at DIG Shibuya - main view',
    caption: 'Echo at DIG Shibuya, Tokyo 2025',
  },
  {
    src: 'https://prcdn.freetls.fastly.net/release_image/162516/4/162516-4-96747e2f2d256f91176576b0f512e004-1920x1280.png?format=jpeg&auto=webp&fit=bounds&width=1200&height=800',
    alt: 'Echo installation - interactive experience',
    caption: 'インタラクティブ体験の様子',
  },
  {
    src: 'https://public.readdy.ai/ai/img_res/3574d1b1a608325a2985e1336e2d1447.jpg',
    alt: 'Echo installation - night view',
    caption: 'Echo — 夜間のライトアップ',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Creative%20studio%20team%20collaboration%20on%20immersive%20art%20installation%20project%2C%20modern%20workspace%20with%20digital%20screens%20showing%20LED%20light%20designs%2C%20Montreal%20and%20Tokyo%20creative%20partnership%2C%20professional%20team%20meeting%20with%20artistic%20atmosphere%2C%20warm%20ambient%20lighting%20and%20clean%20minimalist%20interior&width=1200&height=800&seq=mirari-bitmoves-collab-009&orientation=landscape',
    alt: 'mirari collaboration',
    caption: 'mirari × BITMOVES コラボレーション',
  },
];

export default function EchoVideoGallery() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  // Safeguard: ensure selectedImage index is always valid
  const safeSelectedIndex = selectedImage !== null ? selectedImage % galleryImages.length : null;

  return (
    <section
      ref={ref}
      id="gallery"
      className="bg-gray-950 py-32 lg:py-40 px-8 lg:px-32"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">
            Project Gallery
          </p>
          <h2 className="text-4xl lg:text-5xl font-light text-white mb-4">
            Gallery &amp; Video
          </h2>
          <p className="text-base text-gray-400 max-w-2xl">
            Echoのプロジェクト映像と設置写真をご覧ください。
          </p>
        </div>

        {/* YouTube Video */}
        <div
          className={`mb-16 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div
            className="relative w-full rounded-2xl overflow-hidden"
            style={{ paddingBottom: '56.25%' }}
          >
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube.com/embed/WLxfjdY4g24?rel=0&modestbranding=1"
              title="Echo by Mirari × BITMOVES | DIG SHIBUYA 2025"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <p className="text-sm text-gray-500 mt-4 text-center">
            Echo by mirari × BITMOVES — 渋谷を魅了した没入型インタラクション |
            DIG SHIBUYA 2025
          </p>
        </div>

        {/* Photo Gallery Grid */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-6 transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {galleryImages.map((image, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setSelectedImage(index)}
              className={`group relative w-full h-[320px] rounded-xl overflow-hidden cursor-pointer transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{
                transitionDelay: isVisible ? `${index * 100 + 400}ms` : '0ms',
              }}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-sm text-white">{image.caption}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {safeSelectedIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-8"
          onClick={() => setSelectedImage(null)}
        >
          <button
            type="button"
            onClick={() => setSelectedImage(null)}
            className="absolute top-8 right-8 w-12 h-12 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-3xl" />
          </button>

          {/* Previous */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImage((prev) =>
                prev !== null
                  ? (prev - 1 + galleryImages.length) % galleryImages.length
                  : null
              );
            }}
            className="absolute left-6 w-12 h-12 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-3xl" />
          </button>

          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={galleryImages[safeSelectedIndex].src}
              alt={galleryImages[safeSelectedIndex].alt}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-sm text-gray-400 text-center mt-4">
              {galleryImages[safeSelectedIndex].caption}
            </p>
          </div>

          {/* Next */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImage((prev) =>
                prev !== null ? (prev + 1) % galleryImages.length : null
              );
            }}
            className="absolute right-6 w-12 h-12 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-3xl" />
          </button>
        </div>
      )}
    </section>
  );
}
